import { useState } from 'react'
import type { ReactNode } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { trackSubscribe, trackLogin } from '../../analytics/posthog'
import { useLogin } from '../../auth/useLogin'
import { useI18n, useT } from '../../i18n/useT'
import { formatRenewalDate } from '../../lib/creditRenewal'
import { savePendingPlan } from '../../lib/pendingCheckout'
import type { SubscriptionPlan } from '../../types/subscription'
import { TIER_ACTION, TierCard } from './TierCard'

interface PlanCardProps {
  plan: SubscriptionPlan
  action: 'current' | 'subscribe' | 'switch'
  unit: string
  /** The day this subscription bills again — `creditsReturnOn` has already
   * dropped a date we don't have or that has gone past, so a value here is
   * always worth printing. */
  renewsOn?: Date | null
  onSubscribe: (planName: string) => void
  onCancelSubscription?: () => void
}

export function PlanCard({
  plan,
  action,
  unit,
  renewsOn = null,
  onSubscribe,
  onCancelSubscription,
}: PlanCardProps) {
  const { isAuthenticated } = useAuth0()
  const login = useLogin()
  const { locale } = useI18n()
  const t = useT()
  // Anonymous click → Auth0 round trip. Until the redirect happens the button
  // must visibly react: prod data showed 77% of repeat presses within 5 s —
  // people hammering a button that looked dead.
  const [redirecting, setRedirecting] = useState(false)

  const isCurrent = action === 'current'

  function handleClick() {
    if (isCurrent || redirecting) return

    trackSubscribe(plan.name, isAuthenticated)

    if (!isAuthenticated) {
      // Credits live on an account, so sign-in has to come first — but the
      // chosen plan is remembered and offered again right after login, and
      // a would-be buyer most likely has no account yet: open the signup
      // screen, not the login one.
      trackLogin()
      savePendingPlan(plan.name)
      setRedirecting(true)
      setTimeout(() => login({ returnTo: '/#pricing', screenHint: 'signup' }), 150)
      return
    }

    onSubscribe(plan.name)
  }

  // The action and the line under it are decided together, and handed to the
  // card separately: TierCard reserves the line's height on every rung, which
  // is what keeps the four buttons on one row.
  const [control, hint] = ((): [ReactNode, ReactNode] => {
    if (isCurrent) {
      if (!onCancelSubscription) return [null, null]
      return [
        <button
          onClick={onCancelSubscription}
          className={`${TIER_ACTION} border border-error text-error hover:bg-error/10 transition-colors cursor-pointer`}
        >
          {t('pricing.cancel')}
        </button>,
        // No stand-in when the date is missing: a promise without a day is
        // worse than no promise, and the row keeps its shape either way.
        renewsOn ? t('pricing.billRenews', { date: formatRenewalDate(renewsOn, locale) }) : null,
      ]
    }
    if (action === 'switch') {
      // Already on another plan — buying this one would stack a second
      // subscription, so the button waits for a cancellation
      return [
        <button disabled className={`${TIER_ACTION} bg-primary/40 text-on-accent cursor-not-allowed`}>
          {t('pricing.subscribe')}
        </button>,
        t('pricing.switchHint'),
      ]
    }
    return [
      <button
        onClick={handleClick}
        disabled={redirecting}
        aria-busy={redirecting}
        className={`${TIER_ACTION} text-on-accent transition-all duration-200 ${
          redirecting ? 'bg-primary/70 cursor-wait' : 'bg-primary hover:bg-primary-hover cursor-pointer'
        }`}
      >
        {redirecting ? (
          <span className="inline-flex items-center justify-center gap-1.5">
            <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            {t('pricing.redirecting')}
          </span>
        ) : (
          t('pricing.subscribe')
        )}
      </button>,
      redirecting ? <span role="status">{t('pricing.signInFirst', { plan: plan.name })}</span> : null,
    ]
  })()

  return (
    <TierCard
      name={plan.name}
      price={plan.price}
      priceNote={plan.priceNote}
      count={plan.credits}
      unit={unit}
      // Terms of payment, once per card, where the feature list used to be:
      // "Cancel Anytime" was never a feature of the product, and paid
      // transcriptions carrying over is a real difference from the free ones
      bill={t('pricing.billPaid')}
      badge={
        isCurrent
          ? { text: t('pricing.active'), tone: 'current' }
          : plan.highlighted
            ? { text: t('pricing.popular'), tone: 'primary' }
            : undefined
      }
      highlighted={plan.highlighted && !isCurrent}
      own={isCurrent}
      hint={hint}
    >
      {control}
    </TierCard>
  )
}
